<template>
  <v-row justify="end" class="mr-12">
    <v-dialog
      v-model="dialog"
      persistent
      width="1024"
      @click:outside="dialog = false"
    >
      <template #activator="{ props }">
        <v-btn
          color="#004984"
          v-bind="props"
          class="text-capitalize text-white button-config"
        >
          Cadastrar Requisito
          <v-icon right class="ml-1">mdi-plus</v-icon>
        </v-btn>
      </template>

      <v-card>
        <v-card-title>
          <div class="d-flex justify-space-between">
            <span class="text-h5">Cadastrar Requisito</span>
            <v-btn
              variant="plain"
              rounded="xl"
              prepend-icon="mdi-window-close"
              @click="dialog = false"
            ></v-btn>
          </div>
        </v-card-title>

        <v-card-text>
          <v-container>
            <v-form ref="form" validate-on="lazy submit">
              <v-row>
                <v-col cols="12" sm="3" md="6">
                  <v-select
                    v-model="identificacao"
                    :items="['RF', 'RNF']"
                    label="Identificação*"
                    required
                    :rules="tipoRequisitoRules"
                  ></v-select>
                </v-col>

                <v-col cols="12" sm="6" md="6">
                  <v-select
                    :disabled="isDisabled"
                    v-model="tipo_requisito"
                    :items="[
                      'Restrição organizacional',
                      'Restrição externa',
                      'Restrição qualidade',
                    ]"
                    label="Tipo Requisito*"
                    required
                    :rules="tipoRequisitoRules"
                  ></v-select>
                </v-col>

                <v-col cols="12">
                  <v-textarea
                    v-model="descricao"
                    required
                    label="Descrição*"
                    placeholder="Descrição do requisito"
                    rows="3"
                    :rules="descricaoRules"
                  ></v-textarea>
                </v-col>

                <v-col cols="12">
                  <v-textarea
                    v-model="observacao"
                    required
                    label="Observação"
                    placeholder="Informações adicionais sobre o requisito"
                    rows="3"
                    :rules="observacaoRules"
                  ></v-textarea>
                </v-col>
              </v-row>
            </v-form>
          </v-container>
          <small>*Campos obrigatórios</small>
        </v-card-text>

        <v-card-actions>
          <v-spacer></v-spacer>

          <v-btn color="blue-darken-1" variant="text" @click="submitForm">
            Salvar
            <v-progress-circular
              v-if="this.isLoading"
              class="ml-2"
              indeterminate
              size="20"
            />
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-row>
</template>

<script>
/**
 * @module ModalCriarRequisito
 * @desc Modal para realizar a edição de um determinado requisito
 * @vue-data {string} [projetoID=null] - ID do projeto.
 * @vue-data {string} [csuID=null] - ID do caso de uso.
 * @vue-data {string} [id_requisito=null] - ID do requisito.
 * @vue-data {string} [identificacao=''] - A identificação do requisito.
 * @vue-data {string} [descricao=''] - A descrição do requisito.
 * @vue-data {boolean} [dialog=false] - Define se a caixa de diálogo
 * está aberta ou fechada.
 * @vue-data {boolean} [ativo=false] - Define se o requisito começa ativo.
 * @vue-data {string} [observacao=''] - A observação do requisito.
 * @vue-data {string} [tipo_requisito=''] - O tipo do requisito.
 * @vue-data {boolean} [isLoading=false] - Flag que indica se o requisito
 * ainda está sendo criado.
 */
import axios from 'axios';

import store from '@/store/store';

export default {
  emits: ['requisitoCriado'],
  data: () => ({
    csuID: null,
    dialog: false,
    identificacao: '',
    descricao: '',
    // eslint-disable-next-line camelcase
    tipo_requisito: '',
    observacao: '',
    idProjeto: '',
    isLoading: false,
    tipoRequisitoRules: [(v) => !!v || 'Tipo de requisito é obrigatório'],
    descricaoRules: [(v) => !!v.trim() || 'Descrição é obrigatória'],
    observacaoRules: [
      (v) => v.length <= 100 || 'Observação deve ter no máximo 500 caracteres',
    ],
  }),
  computed: {
    isDisabled() {
      this.clearDisabledText();

      return this.identificacao === 'RF';
    },
  },
  created() {
    this.csuID = this.$route.params.id;
    this.idProjeto = Number(this.$route.params.idProjeto);
  },
  mounted() {
    window.addEventListener('keydown', this.handleKeyDown);
  },
  beforeUnmount() {
    window.removeEventListener('keydown', this.handleKeyDown);
  },
  methods: {
    /**
     * Formata as strings do enum;
     */
    converterTextoParaEnum(string) {
      /// TIPO REQUISITO.
      if (string === 'Restrição funcional') {
        return 'RESTRICAO_FUNCIONAL';
      } else if (string === 'Restrição organizacional') {
        return 'RESTRICAO_ORGANIZACIONAL';
      } else if (string === 'Restrição externa') {
        return 'RESTRICAO_EXTERNA';
      } else if (string === 'Restrição qualidade') {
        return 'RESTRICAO_QUALIDADE';
      }
    },
    converterEnumParaTexto(string) {
      /// TIPO REQUISITO.
      if (string === 'RESTRICAO_FUNCIONAL') {
        return 'Restrição funcional';
      } else if (string === 'RESTRICAO_ORGANIZACIONAL') {
        return 'Restrição organizacional';
      } else if (string === 'RESTRICAO_EXTERNA') {
        return 'Restrição externa';
      } else if (string === 'RESTRICAO_QUALIDADE') {
        return 'Restrição qualidade';
      }
    },
    clearDisabledText() {
      // eslint-disable-next-line camelcase
      this.tipo_requisito =
        this.identificacao === 'RF' ? 'Restrição funcional' : '';
    },

    checkIfIdentificacaoIsNull() {
      if (this.identificacao == null) {
        // eslint-disable-next-line camelcase
        this.tipo_requisito = '';
      }
    },
    /**
     * Lida com o evento de pressionar tecla. Se a tecla pressionada for ESC,
     * o modal é fechado.
     *
     * @param {Object} event Objeto do evento
     */
    handleKeyDown(event) {
      if (event.key === 'Escape') {
        this.dialog = false;
      }
    },

    /**
     * Envia o formulário.
     */
    async submitForm() {
      const isValid = (await this.$refs.form.validate()).valid;

      if (isValid) {
        const data = {
          // eslint-disable-next-line camelcase
          id_versao_anterior: null,
          identificacao: this.identificacao,
          descricao: this.descricao,
          // eslint-disable-next-line camelcase
          id_projeto: this.idProjeto,
          // eslint-disable-next-line camelcase
          dt_cadastro: new Date().toISOString(),
          // eslint-disable-next-line camelcase
          tipo_requisito: this.converterTextoParaEnum(this.tipo_requisito),
          observacao: this.observacao,
        };

        try {
          this.isLoading = true;
          const apiUrl = process.env.VUE_APP_API_URL;
          const response = await axios.post(
            `${apiUrl}/api/v1/requisitos/projeto/${this.idProjeto}`,
            data,
            {
              headers: {
                Authorization: `Bearer ${store.state.token}`,
              },
            },
          );

          this.$toast.success(response.data.msg);
          this.$emit('requisitoCriado');
        } catch (error) {
          console.log(error);
          this.$toast.error(error.response.data.msg);
        } finally {
          this.isLoading = false;
          this.dialog = false;
          this.limpaFormulario();
        }
      }
    },

    /**
     * Método auxiliar para limpar os campos do formulário após o seu envio.
     */
    limpaFormulario() {
      this.identificacao = '';
      this.descricao = '';
      // eslint-disable-next-line camelcase
      this.tipo_requisito = '';
      this.observacao = '';
    },
  },
};
</script>

<style scoped>
.button-config {
  background-color: #004984;
}
</style>
